import {
  DefaultError,
  InfiniteData,
  QueryClient,
  QueryKey,
  UndefinedInitialQueryOptions,
  UseInfiniteQueryOptions,
  useInfiniteQuery as useTanstackInfiniteQuery,
  useQuery as useTanstackQuery,
} from '@tanstack/vue-query';
import { watch, type MaybeRef } from 'vue';
import { useToast } from './useToast';

type ToastMeta = {
  showErrorToast?: boolean;
};

type OptionsWithMeta<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = UndefinedInitialQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey
> & {
  // Expose the key directly so it can be inferred through Vue Query's reactive option types.
  queryKey: MaybeRef<TQueryKey>;
  meta?: ToastMeta;
};

export const useQueryWithToast = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: OptionsWithMeta<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient,
) => {
  const { showError } = useToast();
  const query = useTanstackQuery(options, queryClient);

  watch(
    () => query.error.value,
    () => {
      if (!query.error.value) return;
      if (options.meta?.showErrorToast === false) return;
      showError(query.error.value);
    },
    { immediate: true },
  );

  return query;
};

type InfiniteOptionsWithMeta<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TPageParam,
> = UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey, TPageParam> & {
  meta?: ToastMeta;
};

export const useInfiniteQueryWithToast = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: InfiniteOptionsWithMeta<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  queryClient?: QueryClient,
) => {
  const { showError } = useToast();
  const query = useTanstackInfiniteQuery(options, queryClient);

  watch(
    () => query.error.value,
    () => {
      if (!query.error.value) return;
      if (options.meta?.showErrorToast === false) return;
      showError(query.error.value);
    },
    { immediate: true },
  );

  return query;
};
